import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Logo2 } from "../static";
import { api, url } from "../axios/api";
import { ArrowLeft, CalendarDays, CheckCircle2, Download, KeyRound, ShieldCheck } from "lucide-react";

const LicenseRenew = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);

  const [codigo, setCodigo] = useState("");
  const [expireAt, setExpireAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);

  const empresa_id = user?.empresa_id;
  const createdAt = new Date().toISOString().slice(0, 10);

  const handleValidateCode = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.post("/auth/license/validate-code", { codigo });
      if (!response.data.valid && !response.data.habilitado) {
        throw new Error(response.data.message || "Codigo invalido.");
      }
      setValidated(true);
      Swal.fire("Codigo validado", "Agora defina o prazo da licenca.", "success");
    } catch (error) {
      setValidated(false);
      Swal.fire("Erro", error.response?.data?.error || error.message || "Codigo invalido.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLicense = async (event) => {
    event.preventDefault();
    if (!validated) return Swal.fire("Atencao", "Valide o codigo antes de ativar a licenca.", "warning");
    if (!expireAt) return Swal.fire("Atencao", "Selecione a data de expiracao.", "warning");

    setLoading(true);
    try {
      const response = await api.post("/auth/license/activate", {
        empresa_id,
        codigo,
        createdAt,
        expireAt,
      });

      if (response.data.fileName) {
        const link = document.createElement("a");
        link.href = `${url}/mediafiles/${response.data.fileName}`;
        link.setAttribute("download", response.data.fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      await Swal.fire("Licenca ativa", "Licenca gerada e ativada com sucesso.", "success");
      navigate("/dashboard");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || error.message || "Falha ao ativar licenca.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light position-relative overflow-hidden">
      <div className="position-absolute top-0 end-0 p-5 opacity-10">
        <KeyRound size={260} className="text-primary" />
      </div>

      <div className="container z-1">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-5 col-xl-4">
            <div className="license-card">
              <div className="license-titlebar">
                <span>Facturize SAFT</span>
                <ShieldCheck size={16} />
              </div>

              <div className="p-4">
                <div className="text-center mb-4">
                  <img src={Logo2} style={{ width: "58px" }} alt="Logo" className="mb-3" />
                  <h3 className="fw-bold h4 mb-1">Licenca</h3>
                  <p className="text-muted small mb-0">Validar codigo, definir prazo e ativar.</p>
                </div>

                <form onSubmit={validated ? handleCreateLicense : handleValidateCode}>
                  <div className="mb-3">
                    <label className="small fw-bold text-muted">CODIGO DA LICENCA</label>
                    <div className="input-group">
                      <span className="input-group-text rounded-0 bg-white"><KeyRound size={18} /></span>
                      <input
                        type="text"
                        className="form-control rounded-0"
                        placeholder="facturize_saft_25"
                        required
                        value={codigo}
                        onChange={(event) => {
                          setCodigo(event.target.value);
                          setValidated(false);
                        }}
                      />
                      <button type="button" className="btn btn-outline-primary rounded-0" onClick={handleValidateCode} disabled={loading || !codigo}>
                        {validated ? <CheckCircle2 size={16} /> : "Validar"}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="small fw-bold text-muted">VALIDA ATE</label>
                    <div className="input-group">
                      <span className="input-group-text rounded-0 bg-white"><CalendarDays size={18} /></span>
                      <input
                        type="date"
                        className="form-control rounded-0"
                        min={createdAt}
                        required
                        disabled={!validated}
                        value={expireAt}
                        onChange={(event) => setExpireAt(event.target.value)}
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg w-100 fw-bold rounded-0 d-flex align-items-center justify-content-center gap-2" disabled={loading}>
                    {loading ? "Processando..." : validated ? <><Download size={18} /> Gerar e ativar</> : "Validar codigo"}
                  </button>
                </form>

                <div className="text-center mt-4">
                  <button onClick={() => navigate("/login")} className="btn btn-link text-muted text-decoration-none small border-0">
                    <ArrowLeft size={14} className="me-1" /> Voltar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .license-card { background: #fff; border: 1px solid #0078d4; box-shadow: 0 18px 45px rgba(0,0,0,.14); }
        .license-titlebar { height: 34px; display: flex; align-items: center; justify-content: space-between; padding: 0 10px; border-bottom: 1px solid #e1dfdd; font-size: 12px; font-weight: 700; color: #323130; }
        .form-control:focus { border-color: #0078d4; box-shadow: none; }
      `}</style>
    </div>
  );
};

export default LicenseRenew;
